/**
 * DatevExportDialog — M7 DATEV-Buchungsstapel-Export (Präsentation).
 *
 * Reines Präsentations-/Interaktions-Modal: KEINE eigene Firebase-Datenladung —
 * alle Belege kommen als Props (Testbarkeit, ein Query-Pfad). Die EXTF-Erzeugung
 * delegiert vollständig an die reine `buildDatevExport`-Funktion.
 *
 * Zeigt sichtbar (PFLICHT):
 *   - Zeitraum-Wähler (Monat/Quartal/Jahr, Muster ExpenseSummaryBar),
 *   - SKR03/04-Wahl (Default aus Seller-Setting),
 *   - Bucket-Warnung „{{count}} Belege nicht zugeordnet" + Ausschlussliste,
 *   - gerenderter Haftungs-Disclaimer (jurist-Auflage 1, 1:1 aus datev.json),
 *   - DSGVO-Hinweis,
 *   - Berater-/Mandanten-Nr Pflicht-Block wenn leer (A7 — Export geblockt),
 *   - Download-Button → CP1252-`Uint8Array` als `.csv`.
 *
 * @module DatevExportDialog
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconCsv,
  IconClose,
  IconWarning,
  IconError,
  IconDownload,
  IconGobd,
  IconInfo,
  IconFileArchive,
  IconAdvisor,
} from '../../icons';

import {
  buildDatevExport,
  buildDatevFilename,
  DatevInvoiceInput,
  DatevExpenseInput,
  DatevTravelInput,
  DatevHeaderConfig,
  DatevExclusionReason,
  DatevExportResult,
} from '../../utils/datevExport';
import { buildDatevBlob } from '../../utils/datevCp1252';
import { SkrKontenrahmen } from '../../utils/datevMapping';
import { resolvePeriod, SummaryPeriod, SummaryPeriodKind } from '../../utils/expenseSummary';
import { centsToEuros } from '../../utils/moneyUtils';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import { downloadBlob } from '../../api/invoiceApi';
import logger from '../../utils/logger';
import {
  dismissAdvisorInviteCta,
  isAdvisorInviteCtaDismissed,
} from '../../utils/advisorInviteCtaSuppression';

// ─── Props ──────────────────────────────────────────────────────────────────────

export interface DatevExportDialogProps {
  /** Bereits geladene + gemappte Ausgangsrechnungen (Page-Loader, M0-scoped). */
  invoices: DatevInvoiceInput[];
  /** Bereits geladene + gemappte Ausgaben. */
  expenses: DatevExpenseInput[];
  /**
   * RK-8: bereits geladene + gemappte (settled/cancelled) Reise-Pauschalen (§3.5/§8
   * architecture-reisekosten.md). ADDITIV-OPTIONAL — kein Prop bzw. `[]` ⇒ identisches
   * Verhalten zu heute (Null-Delta-Garantie, `buildDatevExport`s 5. Param). Die
   * Page entscheidet über die Sichtbarkeits-Gate (`canUseTravelExpenses`); der Dialog
   * selbst importiert kein Auth (Modul-Kopf).
   */
  travel?: DatevTravelInput[];
  /** Seller-Settings: SKR-Rahmen + Berater-/Mandanten-Nr. (Default-Vorbelegung). */
  sellerDatevConfig: { skr: SkrKontenrahmen; beraterNr: string | null; mandantenNr: string | null };
  /** Schließt das Modal. */
  onClose: () => void;
  /**
   * Optional: Callback zum Persistieren der eingegebenen DATEV-Konfiguration ins
   * Verkäuferprofil (best-effort, fire-and-forget). Wird NACH erfolgreichem Download
   * aufgerufen, nur wenn Werte sich gegenüber `sellerDatevConfig` geändert haben.
   * Der Dialog importiert kein useAuth — die Page implementiert diesen Callback.
   */
  onPersistDatevConfig?: (cfg: {
    skr: SkrKontenrahmen;
    beraterNr: string;
    mandantenNr: string;
  }) => void;
  /**
   * M7b: fail-closed Sichtbarkeit des „Belege-Paket herunterladen (ZIP)"-Buttons
   * (Beta-Allowlist/Flag, `canUseDatevBelege(effectiveScopeUid, tier, isAdvisorContext)`). Default `false`
   * (kein Prop → kein Button) — der bestehende CSV-Export bleibt davon unberührt.
   */
  canUseBelege?: boolean;
  /**
   * M7b: Page-Handler für den ZIP-Download. Erhält den bereits vom Dialog gebauten
   * `DatevExportResult` + `DatevHeaderConfig` (Zeitraum/SKR/Berater-Nr. sind
   * Dialog-lokaler Zustand) als Argumente — die Page baut daraus den
   * `DatevBelegePaketRequest` und ruft `downloadDatevBelegePaket`. Analog zum
   * bestehenden `onPersistDatevConfig`-Muster (Dialog liefert die Daten, Page
   * implementiert die eigentliche Aktion).
   *
   * ABWEICHUNG von architecture-m7b.md (dort `() => void | Promise<void>`,
   * dokumentiert in changelog.md): `result`/`header` sind reiner Dialog-Zustand
   * (Zeitraum-Wahl, SKR, Berater-/Mandanten-Nr.) — eine parameterlose Signatur
   * hätte eine Zustands-Duplikation in der Page erzwungen.
   */
  onDownloadBelegePaket?: (
    result: DatevExportResult,
    header: DatevHeaderConfig,
  ) => void | Promise<void>;
  /** M7b: Ladezustand des Belege-Paket-Downloads (Page-verwaltet). */
  belegeDownloading?: boolean;
  /** M7b: Fehlermeldung des letzten Belege-Paket-Downloads (Page-verwaltet, best-effort). */
  belegeError?: string | null;
  /**
   * M7b: Anzahl der von der Function best-effort übersprungenen Belege des letzten
   * erfolgreichen Downloads (`X-Datev-Belege-Skipped`), oder `null` vor dem ersten
   * Download. Nur informativ — bricht nie den Download ab.
   */
  belegeSkippedCount?: number | null;
  /**
   * WL-F1 (Premium-Trigger 4 "Kanzlei-Übergabe", modules-loop-b.md): optionaler
   * Callback, den der Dialog NACH einem erfolgreichen CSV-Export (Buchungen
   * vorhanden, Berater-/Mandanten-Nr. gesetzt) aufruft — der "Export-Moment".
   * Die Page entscheidet, ob/welche Paywall-Präsentation daraus folgt (Beta-Gate,
   * Premium-Status, Cooldown liegen NICHT im Dialog — reine Präsentationskomponente,
   * Modul-Kopf). Kein Prop → kein Callback (additiv, bestehende Aufrufer unberührt).
   */
  onExportSuccess?: () => void;
  /**
   * WL-GTM-7 (c) "Import-Anleitung-Footer": zeigt eine dezente, dismissbare
   * Zeile mit Link zu `/dashboard/konto/steuerberater` direkt bei den
   * Import-Hinweisen (nahe `belege.importHint`). Der Dialog trägt selbst
   * KEINE Auth-/Beta-/Funnel-Gates (Modul-Kopf, "Der Dialog importiert kein
   * useAuth") — die Page entscheidet, OB die Zeile angeboten wird; der
   * Dialog verwaltet nur den rein lokalen, permanenten Dismiss-Zustand
   * (`advisorInviteCtaSuppression`, kein Auth nötig). Default `false` (kein
   * Prop → keine Zeile, additiv, bestehende Aufrufer unberührt).
   */
  showAdvisorInviteFooterCta?: boolean;
  /**
   * BUG-3 (tester): Navigations-Callback für den F6-„Steuerart jetzt festlegen"-
   * Deep-Link in der Ausschlussliste. Der Dialog navigiert NICHT selbst (kein
   * `<Link>`/`useNavigate` — sonst crasht er ohne Router-Kontext, "Cannot
   * destructure property 'basename'"); die einbettende Seite kennt den Router
   * und führt die Navigation zur Ausgaben-Seite aus. Kein Prop → kein Link
   * (additiv, bestehende Aufrufer unberührt).
   */
  onNavigateToExpense?: () => void;
}

// ─── Local pure helpers ──────────────────────────────────────────────────────────

/** Zwei-stellige Null-gepaddete Zahl. */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Heutiges Datum als ISO YYYY-MM-DD (lokale Zeitzone). */
function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Erzeugt auswählbare konkrete Perioden je Granularität (Muster ExpenseSummaryBar). */
function buildPeriodOptions(kind: SummaryPeriodKind, todayISO: string): SummaryPeriod[] {
  const year = Number.parseInt(todayISO.slice(0, 4), 10);
  const month = Number.parseInt(todayISO.slice(5, 7), 10);
  const anchors: string[] = [];

  if (kind === 'year') {
    for (let i = 0; i < 5; i += 1) anchors.push(`${year - i}-06-15`);
  } else if (kind === 'quarter') {
    const q = Math.ceil(month / 3);
    const absQ = year * 4 + (q - 1);
    for (let i = 0; i < 8; i += 1) {
      const a = absQ - i;
      const yy = Math.floor(a / 4);
      const qq = (a % 4) + 1;
      const midMonth = (qq - 1) * 3 + 2;
      anchors.push(`${yy}-${pad2(midMonth)}-15`);
    }
  } else {
    const absM = year * 12 + (month - 1);
    for (let i = 0; i < 12; i += 1) {
      const a = absM - i;
      const yy = Math.floor(a / 12);
      const mm = (a % 12) + 1;
      anchors.push(`${yy}-${pad2(mm)}-15`);
    }
  }

  return anchors.map((anchor) => resolvePeriod(kind, anchor));
}

// ─── Component ──────────────────────────────────────────────────────────────────

/**
 * DATEV-Export-Modal. Fail-closed: wird nur von einer flag-gated Page eingebettet.
 */
const DatevExportDialog: React.FC<DatevExportDialogProps> = ({
  invoices,
  expenses,
  travel = [],
  sellerDatevConfig,
  onClose,
  onPersistDatevConfig,
  canUseBelege = false,
  onDownloadBelegePaket,
  belegeDownloading = false,
  belegeError = null,
  belegeSkippedCount = null,
  onExportSuccess,
  showAdvisorInviteFooterCta = false,
  onNavigateToExpense,
}) => {
  const { t, i18n } = useTranslation('datev');
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language || 'de-DE';
  // WL-GTM-7 (c): rein lokaler, permanenter Dismiss-Zustand (kein Auth nötig,
  // s. Prop-Kommentar). `useState(() => …)` liest den localStorage-Flag nur
  // beim ersten Mount, nicht bei jedem Render.
  const [advisorInviteFooterDismissed, setAdvisorInviteFooterDismissed] = useState(() =>
    isAdvisorInviteCtaDismissed('import_footer'),
  );
  const todayISO = useMemo(() => getTodayISO(), []);
  // Erzeugungs-Zeitstempel einmal pro Dialog-Lebenszyklus.
  const generatedAtISO = useMemo(() => new Date().toISOString(), []);

  const [kind, setKind] = useState<SummaryPeriodKind>('quarter');
  const [period, setPeriod] = useState<SummaryPeriod>(() => resolvePeriod('quarter', todayISO));
  const [skr, setSkr] = useState<SkrKontenrahmen>(sellerDatevConfig.skr ?? 'SKR03');
  const [beraterNr, setBeraterNr] = useState<string>(sellerDatevConfig.beraterNr ?? '');
  const [mandantenNr, setMandantenNr] = useState<string>(sellerDatevConfig.mandantenNr ?? '');

  // ── Late-fill refs — track user-initiated edits so sellerDatevConfig arriving
  // asynchronously (cold-load: sellerData from Firestore lands after mount) does not
  // overwrite values the user has already typed or chosen in this dialog session.
  const beraterNrEdited = useRef(false);
  const mandantenNrEdited = useRef(false);
  const skrEdited = useRef(false);

  // Auto-fill when sellerDatevConfig arrives late. Each effect is gated on its
  // "edited" ref so a running user input is never silently overwritten.
  useEffect(() => {
    if (!beraterNrEdited.current && sellerDatevConfig.beraterNr != null) {
      setBeraterNr(sellerDatevConfig.beraterNr);
    }
  }, [sellerDatevConfig.beraterNr]);

  useEffect(() => {
    if (!mandantenNrEdited.current && sellerDatevConfig.mandantenNr != null) {
      setMandantenNr(sellerDatevConfig.mandantenNr);
    }
  }, [sellerDatevConfig.mandantenNr]);

  useEffect(() => {
    if (!skrEdited.current) {
      setSkr(sellerDatevConfig.skr ?? 'SKR03');
    }
  }, [sellerDatevConfig.skr]);

  const periodOptions = useMemo(() => buildPeriodOptions(kind, todayISO), [kind, todayISO]);

  const header: DatevHeaderConfig = useMemo(
    () => ({
      beraterNr: beraterNr.trim().length > 0 ? beraterNr.trim() : null,
      mandantenNr: mandantenNr.trim().length > 0 ? mandantenNr.trim() : null,
      skr,
      sachkontenLaenge: 4,
      fromISO: period.fromISO,
      toISO: period.toISO,
      generatedAtISO,
    }),
    [beraterNr, mandantenNr, skr, period.fromISO, period.toISO, generatedAtISO],
  );

  const result = useMemo(
    // RK-8: `travel` additiv-optional als 5. Param (Null-Delta: `[]` ⇒ byte-identisch
    // zum Aufruf ohne Param, §11 architecture-reisekosten.md). `options` bewusst
    // `undefined` (Bestandsverhalten, `bookedExpensesOnly` default `true`).
    () => buildDatevExport(invoices, expenses, header, undefined, travel),
    [invoices, expenses, header, travel],
  );

  // ── Formatters ────────────────────────────────────────────────────────────────
  const currencyFmt = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      }),
    [locale],
  );
  const formatCents = (cents: number): string => currencyFmt.format(centsToEuros(cents));
  const formatDate = (iso: string): string => formatDateCentral(iso, { locale: i18n.language });

  // ── Period label ──────────────────────────────────────────────────────────────
  const periodLabel = (p: SummaryPeriod): string => {
    if (p.kind === 'year') return t('period.yearLabel', { year: p.year });
    if (p.kind === 'quarter') {
      return t('period.quarterLabel', { quarter: p.quarter, year: p.year });
    }
    return new Date(p.year, (p.month ?? 1) - 1, 1).toLocaleDateString(locale, {
      month: 'long',
      year: 'numeric',
    });
  };

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleKindChange = (nextKind: SummaryPeriodKind): void => {
    setKind(nextKind);
    setPeriod(resolvePeriod(nextKind, todayISO));
  };

  const handlePeriodChange = (fromISO: string): void => {
    const next = periodOptions.find((p) => p.fromISO === fromISO);
    if (next) setPeriod(next);
  };

  const handleDownload = (): void => {
    // Defense-in-depth: guard against programmatic calls that bypass the disabled
    // button attribute (e.g. future refactors, tests triggering the handler directly).
    if (!hasBookings || missingBeraterMandant) return;
    try {
      const blob = buildDatevBlob(result.csvLines);
      downloadBlob(blob, buildDatevFilename(header));
    } catch (err) {
      // Total-Funktion + reiner Encoder → praktisch unerreichbar; defensiv geloggt.
      logger.error('DatevExportDialog: download failed', err);
    }
    // Berater-/Mandanten-Nr. und SKR best-effort ins Verkäuferprofil schreiben
    // (nur wenn sich etwas geändert hat; Fehler darf Export NICHT abbrechen).
    if (onPersistDatevConfig) {
      const trimmedBerater = beraterNr.trim();
      const trimmedMandant = mandantenNr.trim();
      const hasChanged =
        trimmedBerater !== (sellerDatevConfig.beraterNr ?? '') ||
        trimmedMandant !== (sellerDatevConfig.mandantenNr ?? '') ||
        skr !== sellerDatevConfig.skr;
      if (hasChanged && trimmedBerater.length > 0 && trimmedMandant.length > 0) {
        try {
          onPersistDatevConfig({ skr, beraterNr: trimmedBerater, mandantenNr: trimmedMandant });
        } catch (persistErr) {
          logger.error('DatevExportDialog: persist DATEV config failed', persistErr);
        }
      }
    }
    // WL-F1: "Export-Moment" — nur bei einem tatsächlich ausgelösten Download
    // (derselbe Guard wie oben: Buchungen vorhanden + Berater-/Mandanten-Nr.
    // gesetzt). Best-effort, darf den Export selbst nie beeinträchtigen.
    if (onExportSuccess) {
      try {
        onExportSuccess();
      } catch (exportSuccessErr) {
        logger.error('DatevExportDialog: onExportSuccess callback failed', exportSuccessErr);
      }
    }
  };

  const skrShort = skr === 'SKR03' ? '03' : '04';
  const missingBeraterMandant = header.beraterNr === null || header.mandantenNr === null;
  const hasBookings = result.bookedCount > 0;

  // M7b: Belege-Paket-Button — fail-closed dieselben Voraussetzungen wie der
  // CSV-Export (Buchungen vorhanden, Berater-/Mandanten-Nr. gesetzt) PLUS die
  // Beta-Allowlist/Flag-Sichtbarkeit.
  const showBelegeButton = canUseBelege && hasBookings && !missingBeraterMandant;
  // "Buchung immer, Beleglink nur mit Datei" (architecture-m7b.md): Buchungen mit
  // einem Beleg-Ref, der auf Input-Ebene KEINE Datei erwartet (kein pdfPath/receipt).
  const belegloseCount = result.documentRefs.filter((ref) => ref.hasFileExpectation === false).length;

  const handleDownloadBelege = (): void => {
    if (!onDownloadBelegePaket || belegeDownloading) return;
    try {
      const maybePromise = onDownloadBelegePaket(result, header);
      if (maybePromise && typeof (maybePromise as Promise<void>).catch === 'function') {
        (maybePromise as Promise<void>).catch((err) => {
          logger.error('DatevExportDialog: Belege-Paket-Download fehlgeschlagen', err);
        });
      }
    } catch (err) {
      // Defensiv: onDownloadBelegePaket sollte selbst fangen (Page-Verantwortung),
      // aber ein synchroner Wurf darf den Dialog nicht crashen.
      logger.error('DatevExportDialog: Belege-Paket-Download fehlgeschlagen (sync)', err);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('title')}
    >
      <div className="bg-surface border border-hairline rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline sticky top-0 bg-surface z-10">
          <h2 className="text-lg font-bold text-primary flex items-center gap-2">
            <IconCsv size={18} className="text-accent-ink" />
            {t('title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('error.retry')}
            className="text-muted hover:text-primary transition-colors"
          >
            <IconClose />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-muted">{t('subtitle')}</p>

          {/* Controls: period + SKR */}
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col gap-2">
              <span className="text-xs text-faint">{t('period.label')}</span>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-hairline overflow-hidden">
                  {(['month', 'quarter', 'year'] as SummaryPeriodKind[]).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => handleKindChange(k)}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        kind === k
                          ? 'bg-indigo-600 text-white'
                          : 'bg-surface-raised text-muted hover:text-primary'
                      }`}
                    >
                      {t(`period.${k}`)}
                    </button>
                  ))}
                </div>
                <select
                  value={period.fromISO}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  aria-label={t('period.label')}
                  className="rounded-lg border border-hairline bg-surface-raised px-3 py-1.5 text-xs text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {periodOptions.map((p) => (
                    <option key={p.fromISO} value={p.fromISO}>
                      {periodLabel(p)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-xs text-faint">{t('skr.label')}</span>
              <div className="flex rounded-lg border border-hairline overflow-hidden">
                {(['SKR03', 'SKR04'] as SkrKontenrahmen[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { skrEdited.current = true; setSkr(s); }}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      skr === s
                        ? 'bg-indigo-600 text-white'
                        : 'bg-surface-raised text-muted hover:text-primary'
                    }`}
                  >
                    {t(s === 'SKR03' ? 'skr.skr03' : 'skr.skr04')}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Berater / Mandant — Pflichtfelder für DATEV-Import */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-faint">
                {t('beraterNr')}{' '}
                <span className="text-[var(--tone-warning-fg)]" aria-hidden="true">*</span>
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={beraterNr}
                onChange={(e) => { beraterNrEdited.current = true; setBeraterNr(e.target.value); }}
                placeholder={t('beraterNrPlaceholder')}
                className={`rounded-lg border px-3 py-2 text-sm text-primary bg-surface-raised focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  beraterNr.trim().length === 0
                    ? 'border-amber-500/70'
                    : 'border-hairline'
                }`}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-faint">
                {t('mandantenNr')}{' '}
                <span className="text-[var(--tone-warning-fg)]" aria-hidden="true">*</span>
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={mandantenNr}
                onChange={(e) => { mandantenNrEdited.current = true; setMandantenNr(e.target.value); }}
                placeholder={t('mandantenNrPlaceholder')}
                className={`rounded-lg border px-3 py-2 text-sm text-primary bg-surface-raised focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  mandantenNr.trim().length === 0
                    ? 'border-amber-500/70'
                    : 'border-hairline'
                }`}
              />
            </label>
          </div>

          {missingBeraterMandant && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-500/50 bg-[var(--tone-warning-bg)] p-4">
              <IconWarning size={14} className="mt-0.5 flex-shrink-0 text-[var(--tone-warning-fg)]" />
              <p className="text-xs leading-relaxed text-[var(--tone-warning-fg)]">
                {t('beraterMandantRequired')}
              </p>
            </div>
          )}

          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-[rgb(var(--surface-raised-rgb)/0.6)] border border-hairline p-4">
              <p className="text-xs text-muted">{t('summaryBooked')}</p>
              <p className="mt-1 text-2xl font-bold text-[var(--tone-success-fg)] font-mono tabular-nums">
                {result.bookedCount}
              </p>
            </div>
            <div className="rounded-lg bg-[rgb(var(--surface-raised-rgb)/0.6)] border border-hairline p-4">
              <p className="text-xs text-muted">{t('summaryExcluded')}</p>
              <p className="mt-1 text-2xl font-bold text-[var(--tone-warning-fg)] font-mono tabular-nums">
                {result.excludedCount}
              </p>
            </div>
          </div>

          {/* Bucket warning + excluded list */}
          {result.excludedCount > 0 && (
            <div className="rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-[var(--tone-warning-fg)]">
                <IconWarning size={14} />
                {t('bucketWarning', { count: result.excludedCount })}
              </p>
              <p className="mt-1 text-xs text-[var(--tone-warning-fg)]">{t('excludedList.intro')}</p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-faint text-left">
                      <th className="py-1 pr-3 font-medium">{t('excludedList.columns.source')}</th>
                      <th className="py-1 pr-3 font-medium">{t('excludedList.columns.belegnr')}</th>
                      <th className="py-1 pr-3 font-medium">{t('excludedList.columns.date')}</th>
                      <th className="py-1 pr-3 font-medium text-right">
                        {t('excludedList.columns.amount')}
                      </th>
                      <th className="py-1 font-medium">{t('excludedList.columns.reason')}</th>
                    </tr>
                  </thead>
                  <tbody className="text-secondary">
                    {result.excluded.map((item, idx) => (
                      <tr key={`${item.source}-${item.belegnr ?? 'x'}-${idx}`} className="border-t border-hairline">
                        <td className="py-1.5 pr-3 text-muted">
                          {t(
                            // RK-8: dritter Zweig „Reise" additiv (§10 Blast-Radius).
                            item.source === 'invoice'
                              ? 'excludedList.sourceInvoice'
                              : item.source === 'expense'
                                ? 'excludedList.sourceExpense'
                                : 'excludedList.sourceTravel',
                          )}
                        </td>
                        <td className="py-1.5 pr-3 font-mono">{item.belegnr ?? '—'}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{formatDate(item.dateISO)}</td>
                        <td className="py-1.5 pr-3 text-right font-mono whitespace-nowrap">
                          {formatCents(item.grossCents)}
                        </td>
                        <td className="py-1.5 text-[var(--tone-warning-fg)]">
                          {t(`reason.${item.reason as DatevExclusionReason}`)}
                          {/* F6 (architecture-goal-durchstich-fixplan.md §2 F6): Handlungslink nur für
                              Ausgaben mit ungeklärter Steuerart — die vorhandene Ausgaben-Seite ist der
                              Ort, an dem der Beleg (anhand Beleg-Nr. links in der Zeile) gefunden und
                              per bestehendem Korrektur-Flow (Stornieren + Neu-Buchen mit korrekter
                              Steuerart) behoben werden kann. KEIN neuer Auto-Heal, KEIN neues Modal —
                              reuse der bestehenden Route. Für Ausgangsrechnungen/Reisen kein Link:
                              ausgestellte Rechnungen sind nachträglich nicht änderbar (Altdaten). */}
                          {item.source === 'expense' &&
                            (item.reason === 'taxSchemeNeedsConfirm' ||
                              item.reason === 'zeroRateAmbiguous') &&
                            onNavigateToExpense && (
                              <>
                                {' '}
                                {/* BUG-3: Callback statt <Link> — der Dialog braucht
                                    keinen Router-Kontext mehr; die Seite navigiert. */}
                                <button
                                  type="button"
                                  onClick={onNavigateToExpense}
                                  className="font-medium underline hover:opacity-80"
                                >
                                  {t('excludedList.fixLink')}
                                </button>
                              </>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty result hint */}
          {!hasBookings && result.excludedCount === 0 && (
            <p className="text-sm text-faint text-center py-4">{t('emptyResult')}</p>
          )}

          {/* M7b: "gebucht, aber ohne Beleg im Paket" — Buchung bleibt im CSV-Count,
              nur der Beleglink/die Datei fehlt (Fail-closed-Kernentscheidung,
              architecture-m7b.md). Nur relevant, wenn der Belege-Button sichtbar ist. */}
          {showBelegeButton && belegloseCount > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3">
              <IconWarning size={14} className="mt-0.5 flex-shrink-0 text-[var(--tone-warning-fg)]" />
              <p className="text-xs leading-relaxed text-[var(--tone-warning-fg)]">
                {t('belege.noDocumentNote', { count: belegloseCount })}
              </p>
            </div>
          )}

          {/* Disclaimer (PFLICHT, jurist-Auflage 1 — sichtbar gerendert, 1:1 aus datev.json) */}
          <div className="rounded-lg border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.4)] p-4">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-muted">
              <IconGobd size={14} className="mt-0.5 flex-shrink-0 text-faint" />
              <span>{t('disclaimer', { skr: skrShort, excluded: result.excludedCount })}</span>
            </p>
            <p className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-faint">
              <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
              <span>{t('dsgvoNote')}</span>
            </p>
            {showBelegeButton && (
              <p className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-faint">
                <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('belege.dsgvoBelegeNote')}</span>
              </p>
            )}
            {/* M7b-5c Option A: zwei getrennte DATEV-Importkanäle (CSV vs. ZIP). */}
            {showBelegeButton && (
              <p className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-faint">
                <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('belege.importHint')}</span>
              </p>
            )}
            {/* WL-GTM-7 (c) "Import-Anleitung-Footer". Page-gated (Prop), lokal
                permanent dismissbar (X). */}
            {showAdvisorInviteFooterCta && !advisorInviteFooterDismissed && (
              <div className="mt-3 flex items-start gap-2 border-t border-hairline pt-3 text-xs leading-relaxed text-[var(--tone-success-fg)]">
                <IconAdvisor size={14} className="mt-0.5 flex-shrink-0 text-[var(--tone-success-fg)]" />
                <span className="flex-1">{t('advisorInviteFooter.message')}</span>
                <Link
                  to="/dashboard/konto/steuerberater"
                  className="flex-shrink-0 font-semibold text-[var(--tone-success-fg)] underline hover:text-emerald-200"
                >
                  {t('advisorInviteFooter.cta')}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    dismissAdvisorInviteCta('import_footer');
                    setAdvisorInviteFooterDismissed(true);
                  }}
                  aria-label={t('advisorInviteFooter.dismiss')}
                  className="flex-shrink-0 text-[var(--tone-success-fg)] transition-colors hover:text-primary"
                >
                  <IconClose size={12} />
                </button>
              </div>
            )}
          </div>

          {/* M7b: Belege-Paket-Fehler (Page-verwaltet, unabhängig vom CSV-Export). */}
          {belegeError && (
            <div className="flex items-start gap-3 rounded-lg border border-[var(--tone-danger-border)] bg-[var(--tone-danger-bg)] p-3">
              <IconError size={14} className="mt-0.5 flex-shrink-0 text-[var(--tone-danger-fg)]" />
              <p className="text-xs leading-relaxed text-[var(--tone-danger-fg)]">
                {t('belege.downloadError', { message: belegeError })}
              </p>
            </div>
          )}

          {/* M7b: Server-seitig übersprungene Belege des letzten erfolgreichen Downloads. */}
          {showBelegeButton && belegeSkippedCount != null && belegeSkippedCount > 0 && (
            <p className="text-xs text-faint">
              {t('belege.skippedNote', { count: belegeSkippedCount })}
            </p>
          )}

          {/* Download */}
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            {showBelegeButton && (
              <button
                type="button"
                onClick={handleDownloadBelege}
                disabled={belegeDownloading}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                  belegeDownloading
                    ? 'border-hairline bg-surface-raised text-gray-600 cursor-not-allowed'
                    : 'border-indigo-500/60 bg-transparent text-accent-ink hover:bg-indigo-500/10'
                }`}
              >
                <IconFileArchive />
                {belegeDownloading ? t('exporting') : t('belege.button')}
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={!hasBookings || missingBeraterMandant}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                hasBookings && !missingBeraterMandant
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  : 'bg-surface-raised text-gray-600 cursor-not-allowed'
              }`}
            >
              <IconDownload />
              {t('export')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatevExportDialog;
